import React, { useState } from 'react';
import {
    Box,
    Typography,
    Divider,
    Chip,
    useTheme,
    IconButton,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import {
    startOfMonth,
    endOfMonth,
    eachDayOfInterval,
    format,
    startOfWeek,
    addDays,
    isSameMonth,
    subMonths,
    addMonths,
    isToday,
} from 'date-fns';
import { pl } from 'date-fns/locale';
import WidgetCard from './WidgetCard';
import { useNavigate } from 'react-router-dom';

const WeeklyCalendarWidget: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [currentDate, setCurrentDate] = useState(new Date());

    const startMonth = startOfMonth(currentDate);
    const endMonth = endOfMonth(currentDate);
    const startDate = startOfWeek(startMonth, { weekStartsOn: 1 });
    const endDate = addDays(startOfWeek(endMonth, { weekStartsOn: 1 }), 6);

    const days = eachDayOfInterval({ start: startDate, end: endDate });

    const weeks: Date[][] = [];
    for (let i = 0; i < days.length; i += 7) {
        weeks.push(days.slice(i, i + 7));
    }

    const dayNames = ['pn', 'wt', 'śr', 'czw', 'pt', 'sob', 'nd'];

    const handlePrevMonth = () => setCurrentDate((prev) => subMonths(prev, 1));
    const handleNextMonth = () => setCurrentDate((prev) => addMonths(prev, 1));

    // Handler for day click - navigate to calendar page with day view
    const handleDayClick = (day: Date) => {
        // Format date as ISO string for URL parameter
        const dateParam = day.toISOString();
        // Navigate to calendar page with date and view parameters
        navigate(`/calendar?date=${dateParam}&view=day`);
    };

    // Handler for month/year pill click - navigate to calendar page with monthly view
    const handleMonthYearClick = () => {
        // Format current date as ISO string for URL parameter
        const dateParam = currentDate.toISOString();
        // Navigate to calendar page with monthly view
        navigate(`/calendar?date=${dateParam}&view=month`);
    };

    return (
        <WidgetCard>
            {/* Nagłówek z ikoną i zakresem dat */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Box display="flex" alignItems="center">
                    <Box
                        sx={{
                            bgcolor: '#f0f4ff',
                            color: 'primary.main',
                            borderRadius: 999,
                            width: 32,
                            height: 32,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2,
                        }}
                    >
                        <CalendarMonthIcon fontSize="small" />
                    </Box>
                    <Typography variant="subtitle1" fontWeight={600} fontSize="0.95rem">
                        Kalendarz
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center">
                    <IconButton onClick={handlePrevMonth} size="small">
                        <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>
                    <Chip
                        label={format(currentDate, 'LLLL yyyy', { locale: pl })}
                        size="small"
                        variant="outlined"
                        onClick={handleMonthYearClick}
                        sx={{
                            borderRadius: 999,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            color: 'primary.main',
                            borderColor: 'primary.main',
                            mx: 0.5,
                            cursor: 'pointer',
                            '&:hover': {
                                backgroundColor: 'primary.light',
                                opacity: 0.8,
                            },
                        }}
                    />
                    <IconButton onClick={handleNextMonth} size="small">
                        <ArrowForwardIosIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>

            <Divider sx={{ my: 1 }} />

            {/* Nazwy dni tygodnia */}
            <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" mb={1}>
                {dayNames.map((day) => (
                    <Typography key={day} align="center" fontSize="0.75rem" fontWeight={500} color="text.secondary">
                        {day}
                    </Typography>
                ))}
            </Box>

            {/* Dni miesiąca – siatka z liniami + wyróżnienie dzisiejszego dnia */}
            <Box
                display="grid"
                gridTemplateColumns="repeat(7, 1fr)"
                sx={{
                    borderTop: `1px solid ${theme.palette.divider}`,
                    borderLeft: `1px solid ${theme.palette.divider}`,
                }}
            >
                {weeks.flat().map((day, index) => (
                    <Box
                        key={index}
                        onClick={() => handleDayClick(day)}
                        sx={{
                            height: 36,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            fontSize: '0.75rem',
                            color: isToday(day)
                                ? theme.palette.primary.contrastText
                                : isSameMonth(day, currentDate)
                                    ? 'text.primary'
                                    : 'text.disabled',
                            backgroundColor: isToday(day) ? theme.palette.primary.main : 'transparent',
                            borderRight: `1px solid ${theme.palette.divider}`,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            cursor: 'pointer',
                            '&:hover': {
                                backgroundColor: isToday(day) 
                                    ? theme.palette.primary.dark 
                                    : theme.palette.action.hover,
                            },
                        }}
                    >
                        {format(day, 'd')}
                    </Box>
                ))}
            </Box>
        </WidgetCard>
    );
};

export default WeeklyCalendarWidget;
